import { useEffect, useState, useCallback } from "react";
import { useAdminAuth } from "../context/AdminAuthContext";
import { ShieldCheck, LogOut, RefreshCw, Search, UserCheck, UserX, Trash2, Crown, Zap, Clock } from "lucide-react";

const STATUS_STYLES = {
  active: "bg-green-900/50 text-green-300 border-green-800/60",
  pending: "bg-amber-900/50 text-amber-300 border-amber-800/60",
  deactivated: "bg-gray-800 text-gray-400 border-gray-700",
};
const ROLE_STYLES = {
  free: "bg-gray-800 text-gray-300 border-gray-700",
  pro: "bg-indigo-900/60 text-indigo-200 border-indigo-700/50",
};

export default function AdminUsersPage() {
  const { admin, logout, authedAxios } = useAdminAuth();
  const [users, setUsers] = useState([]);
  const [counts, setCounts] = useState({ total: 0, pending: 0, active: 0, deactivated: 0 });
  const [loading, setLoading] = useState(false);
  const [filterStatus, setFilterStatus] = useState("");
  const [filterRole, setFilterRole] = useState("");
  const [q, setQ] = useState("");
  const [err, setErr] = useState(null);

  const load = useCallback(async () => {
    setErr(null);
    setLoading(true);
    try {
      const axi = authedAxios();
      const params = {};
      if (filterStatus) params.status = filterStatus;
      if (filterRole) params.role = filterRole;
      if (q.trim()) params.q = q.trim();
      const res = await axi.get("/admin/users", { params });
      setUsers(res.data.users || []);
      setCounts(res.data.counts || {});
    } catch (e) {
      const d = e?.response?.data?.detail;
      setErr(typeof d === "string" ? d : "Failed to load users");
    } finally {
      setLoading(false);
    }
  }, [authedAxios, filterStatus, filterRole, q]);

  useEffect(() => { load(); }, [load]);

  const act = async (user, action, body) => {
    try {
      const axi = authedAxios();
      if (action === "delete") {
        if (!window.confirm(`Hard delete ${user.email}? This cannot be undone.`)) return;
        await axi.delete(`/admin/users/${user.id}`);
      } else if (action === "approve") {
        await axi.post(`/admin/users/${user.id}/approve`);
      } else if (action === "deactivate") {
        await axi.post(`/admin/users/${user.id}/deactivate`);
      } else if (action === "role") {
        await axi.post(`/admin/users/${user.id}/role`, body);
      } else if (action === "status") {
        await axi.post(`/admin/users/${user.id}/status`, body);
      }
      await load();
    } catch (e) {
      const d = e?.response?.data?.detail;
      alert(typeof d === "string" ? d : `Action failed: ${action}`);
    }
  };

  return (
    <div className="min-h-screen bg-slate-950 text-gray-200" data-testid="admin-users-page">
      {/* Header */}
      <div className="border-b border-red-900/30 bg-gradient-to-r from-slate-950 via-gray-900 to-red-950/30">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between flex-wrap gap-2">
          <div className="flex items-center gap-2 flex-wrap">
            <ShieldCheck className="w-5 h-5 text-red-400" />
            <h1 className="text-lg font-semibold text-white">Admin Console · Users</h1>
            {admin?.username && (
              <div className="flex items-center gap-2 text-[11px] font-mono">
                <span className="text-gray-500">signed in as</span>
                <span className="text-gray-300">{admin.username}</span>
                {admin?.email && (
                  <span className="px-1.5 py-0.5 rounded bg-gray-800 border border-gray-700 text-gray-400">
                    {admin.email}
                  </span>
                )}
              </div>
            )}
          </div>
          <button
            onClick={logout}
            data-testid="admin-logout-button"
            className="inline-flex items-center gap-1 px-3 py-1.5 text-xs rounded border border-gray-700 bg-gray-900 hover:bg-gray-800 text-gray-300"
          >
            <LogOut className="w-3.5 h-3.5" /> Log out
          </button>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 py-6 space-y-4">
        {/* Counts */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
          <StatTile label="Total" value={counts.total} testid="admin-count-total" />
          <StatTile label="Pending" value={counts.pending} color="text-amber-300" testid="admin-count-pending" />
          <StatTile label="Active" value={counts.active} color="text-green-300" testid="admin-count-active" />
          <StatTile label="Deactivated" value={counts.deactivated} color="text-gray-400" testid="admin-count-deactivated" />
        </div>

        {/* Filters */}
        <div className="flex items-center gap-2 flex-wrap bg-gray-900/60 border border-gray-800 rounded p-2">
          <div className="relative flex-1 min-w-[200px]">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-500" />
            <input
              type="text"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search email or username…"
              data-testid="admin-users-search"
              className="w-full pl-8 pr-3 py-1.5 bg-gray-900 border border-gray-700 rounded text-sm focus:outline-none focus:border-indigo-500"
            />
          </div>
          <select
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
            data-testid="admin-users-filter-status"
            className="bg-gray-900 border border-gray-700 rounded px-2 py-1.5 text-sm"
          >
            <option value="">All statuses</option>
            <option value="pending">Pending</option>
            <option value="active">Active</option>
            <option value="deactivated">Deactivated</option>
          </select>
          <select
            value={filterRole}
            onChange={(e) => setFilterRole(e.target.value)}
            data-testid="admin-users-filter-role"
            className="bg-gray-900 border border-gray-700 rounded px-2 py-1.5 text-sm"
          >
            <option value="">All roles</option>
            <option value="free">Free</option>
            <option value="pro">Pro</option>
          </select>
          <button
            onClick={load}
            disabled={loading}
            data-testid="admin-users-refresh"
            className="inline-flex items-center gap-1 px-2 py-1.5 text-xs rounded border border-gray-700 bg-gray-900 hover:bg-gray-800 disabled:opacity-50"
          >
            <RefreshCw className={`w-3.5 h-3.5 ${loading ? "animate-spin" : ""}`} /> Refresh
          </button>
        </div>

        {err && (
          <div className="p-2 text-xs rounded border border-red-900/60 bg-red-950/30 text-red-300" data-testid="admin-users-error">{err}</div>
        )}

        {/* Table */}
        <div className="bg-gray-900/60 border border-gray-800 rounded overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-[11px] uppercase tracking-wide text-gray-500 border-b border-gray-800">
                <th className="text-left px-3 py-2 font-semibold">Email</th>
                <th className="text-left px-3 py-2 font-semibold">Username</th>
                <th className="text-left px-3 py-2 font-semibold">Status</th>
                <th className="text-left px-3 py-2 font-semibold">Role</th>
                <th className="text-left px-3 py-2 font-semibold hidden lg:table-cell">Created</th>
                <th className="text-right px-3 py-2 font-semibold">Actions</th>
              </tr>
            </thead>
            <tbody>
              {users.length === 0 && !loading && (
                <tr><td colSpan={6} className="text-center py-8 text-gray-500 text-sm">No users found.</td></tr>
              )}
              {users.map((u) => (
                <tr key={u.id} data-testid={`admin-user-row-${u.username}`} className="border-b border-gray-800/60 hover:bg-gray-800/30">
                  <td className="px-3 py-2 font-mono text-[12px] text-gray-200">{u.email}</td>
                  <td className="px-3 py-2 text-white font-semibold">{u.username}</td>
                  <td className="px-3 py-2">
                    <span className={`inline-block px-2 py-0.5 rounded border text-[10px] font-mono uppercase ${STATUS_STYLES[u.status] || ""}`}>
                      {u.status}
                    </span>
                  </td>
                  <td className="px-3 py-2">
                    <span className={`inline-block px-2 py-0.5 rounded border text-[10px] font-mono uppercase ${ROLE_STYLES[u.role] || ""}`}>
                      {u.role}
                    </span>
                  </td>
                  <td className="px-3 py-2 hidden lg:table-cell text-[11px] text-gray-500 font-mono">
                    <div className="inline-flex items-center gap-1">
                      <Clock className="w-3 h-3" />
                      {u.created_at ? new Date(u.created_at).toLocaleDateString() : "—"}
                    </div>
                  </td>
                  <td className="px-3 py-2 text-right">
                    <div className="inline-flex items-center gap-1 flex-wrap justify-end">
                      {u.status !== "active" && (
                        <IconBtn onClick={() => act(u, "approve")} testid={`approve-${u.username}`} title="Approve / activate" color="green">
                          <UserCheck className="w-3.5 h-3.5" />
                        </IconBtn>
                      )}
                      {u.status === "active" && (
                        <IconBtn onClick={() => act(u, "deactivate")} testid={`deactivate-${u.username}`} title="Deactivate" color="amber">
                          <UserX className="w-3.5 h-3.5" />
                        </IconBtn>
                      )}
                      {u.role === "free" ? (
                        <IconBtn onClick={() => act(u, "role", { role: "pro" })} testid={`promote-${u.username}`} title="Upgrade to Pro" color="indigo">
                          <Crown className="w-3.5 h-3.5" />
                        </IconBtn>
                      ) : (
                        <IconBtn onClick={() => act(u, "role", { role: "free" })} testid={`demote-${u.username}`} title="Downgrade to Free" color="gray">
                          <Zap className="w-3.5 h-3.5" />
                        </IconBtn>
                      )}
                      <IconBtn onClick={() => act(u, "delete")} testid={`delete-${u.username}`} title="Hard delete" color="red">
                        <Trash2 className="w-3.5 h-3.5" />
                      </IconBtn>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <p className="text-[11px] text-gray-500 text-center">
          Pending users cannot log in until approved. Deactivating an active user locks them out immediately on their next request.
        </p>
      </div>
    </div>
  );
}

function StatTile({ label, value, color = "text-white", testid }) {
  return (
    <div className="rounded border border-gray-800 bg-gray-900/60 p-3" data-testid={testid}>
      <div className="text-[10px] uppercase tracking-wide text-gray-500 font-semibold">{label}</div>
      <div className={`mt-1 text-xl font-bold font-mono ${color}`}>{value ?? 0}</div>
    </div>
  );
}

function IconBtn({ children, onClick, title, color = "gray", testid }) {
  const colorMap = {
    green: "border-green-800/60 text-green-300 hover:bg-green-900/30",
    amber: "border-amber-800/60 text-amber-300 hover:bg-amber-900/30",
    red: "border-red-800/60 text-red-300 hover:bg-red-900/30",
    indigo: "border-indigo-800/60 text-indigo-300 hover:bg-indigo-900/30",
    gray: "border-gray-700 text-gray-400 hover:bg-gray-800",
  };
  return (
    <button
      onClick={onClick}
      title={title}
      data-testid={testid}
      className={`inline-flex items-center gap-1 p-1.5 rounded border bg-gray-900/60 transition-colors ${colorMap[color]}`}
    >
      {children}
    </button>
  );
}
